import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Bot,
  Code2,
  BookOpen,
  BarChart3,
  Ticket,
  Shield,
  Settings,
  ToggleLeft,
  ToggleRight,
  Users,
  MessageSquare,
  Zap,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

/**
 * AI Operations Overview - Admin Dashboard for AI System
 * Phase 1: Shell with navigation to sub-pages
 */

const AIOperationsOverview = () => {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/ai/stats', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch AI stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = user?.role === 'platform_owner' || user?.role === 'platform_admin';

  const statCards = [
    {
      title: 'Dev Agent Tasks',
      value: stats?.tasks || 0,
      subValue: `${stats?.tasks_today || 0} today`,
      icon: Code2,
      color: 'emerald',
      superAdminOnly: true
    },
    {
      title: 'Training Documents',
      value: stats?.training_docs || 0,
      icon: BookOpen,
      color: 'blue'
    },
    {
      title: 'Support Tickets',
      value: stats?.tickets || 0,
      subValue: `${stats?.tickets_today || 0} today`,
      icon: Ticket,
      color: 'amber'
    },
    {
      title: 'Audit Entries',
      value: stats?.audit_entries || 0,
      icon: Shield,
      color: 'purple',
      superAdminOnly: true
    }
  ];

  const navigationCards = [
    {
      title: 'AI Assistant',
      description: 'Configure the client-facing AI help desk. Set tone, limits, and agency assignments.',
      icon: Bot,
      href: '/admin/ai-assistant',
      color: 'emerald',
      status: 'active'
    },
    {
      title: 'Developer Agent',
      description: 'Internal code assistant for fixing and improving the platform. Super Admin only.',
      icon: Code2,
      href: '/admin/dev-agent',
      color: 'blue',
      status: 'beta',
      superAdminOnly: true
    },
    {
      title: 'Prompt / Training',
      description: 'Upload and manage training documents, PDFs, and knowledge base content.',
      icon: BookOpen,
      href: '/admin/ai-training',
      color: 'purple',
      status: 'active'
    },
    {
      title: 'Usage & Cost',
      description: 'Track token usage, costs, and AI system performance metrics.',
      icon: BarChart3,
      href: '/admin/ai-usage',
      color: 'amber',
      status: 'active'
    },
    {
      title: 'Tickets & Escalation',
      description: 'View and manage support tickets created through AI escalation.',
      icon: Ticket,
      href: '/admin/ai-tickets',
      color: 'rose',
      status: 'active'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            AI Operations
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage AI Assistant, Developer Agent, training, and usage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700">
            Phase 1 - Foundation
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          if (stat.superAdminOnly && !isSuperAdmin) return null;
          
          const Icon = stat.icon;
          return (
            <Card key={index} className="dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.12)]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                      {loading ? '...' : stat.value.toLocaleString()}
                    </p>
                    {stat.subValue && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{stat.subValue}</p>
                    )}
                  </div>
                  <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 dark:bg-${stat.color}-900/30 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {navigationCards.map((card, index) => {
          if (card.superAdminOnly && !isSuperAdmin) return null;
          
          const Icon = card.icon;
          return (
            <a
              key={index}
              href={card.href}
              className="block group"
              data-testid={`ai-nav-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Card className="h-full transition-all hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-700 dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.12)]">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-lg bg-${card.color}-100 dark:bg-${card.color}-900/30 flex items-center justify-center group-hover:scale-105 transition-transform`}>
                      <Icon className={`w-5 h-5 text-${card.color}-600 dark:text-${card.color}-400`} />
                    </div>
                    <div className="flex gap-1">
                      {card.status === 'beta' && (
                        <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700">
                          Beta
                        </Badge>
                      )}
                      {card.superAdminOnly && (
                        <Badge variant="outline" className="text-xs bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700">
                          Super Admin
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-lg text-slate-900 dark:text-white mt-3">{card.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-slate-600 dark:text-slate-400">
                    {card.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </a>
          );
        })}
      </div>

      {/* System Status */}
      <Card className="dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.12)]">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 dark:text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            AI System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">AI Assistant</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">Configured & Ready</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Developer Agent</p>
                <p className="text-xs text-amber-600 dark:text-amber-400">Phase 1 - Simulated</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Usage Tracking</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Active & Logging</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-slate-500 dark:text-slate-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Security & Audit</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              All AI operations are logged for audit. Developer Agent requires Super Admin approval before any code changes.
              Secrets and internal logic are never exposed to AI models.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIOperationsOverview;
